/**
 * `/note` and `/todo`: written straight to the offline store, with no agent
 * turn, so they work with no signal (unit 26B, item 7). The grammar is
 * `capture.ts`; this is the write, and the words around it.
 *
 * The reply says where it went and what stuck to it — the whole risk of a
 * one-line capture is a note filed where you will not think to look. An
 * unknown `@name` is never created: one typo would leave a junk notebook.
 */
import { openOffline } from "../offline/idb";
import { createNote } from "../offline/notes";
import { requestDrain } from "../offline/register";
import { createTodo } from "../offline/todos";
import { parseCapture, type Target } from "./capture";

export interface Outcome {
  reply: string;
  error: boolean;
  /** Put back in the box: the write failed, and the text must not be lost. */
  restore?: string;
}

const KINDS = {
  note: { word: "note", noun: "notebook", home: "Misc", lead: "Noted" },
  todo: { word: "to-do", noun: "list", home: "Today", lead: "To do" },
} as const;

const HELP = {
  note:
    "`/note` needs something to write — e.g. `/note ring the dentist`.\n\n" +
    "Shift+Enter starts a second line, `#tag` tags it, `@Notebook` files it.",
  todo:
    "`/todo` needs something to do — e.g. `/todo ring the dentist`.\n\n" +
    "It lands in **Today**. `#tag` tags it, `@List` files it, Shift+Enter adds notes.",
};

export async function capture(
  kind: "note" | "todo",
  text: string,
  targets: readonly Target[],
  { drain = requestDrain, now = new Date() }: { drain?: () => Promise<void>; now?: Date } = {},
): Promise<Outcome> {
  const words = KINDS[kind];
  if (!text) return { reply: HELP[kind], error: true };
  const parsed = parseCapture(text, targets);
  if (parsed.empty) {
    return {
      reply: `That ${words.word} is only tags — there's nothing to save with them.`,
      error: true,
    };
  }
  let title: string;
  try {
    const db = await openOffline();
    try {
      const target = parsed.target_id === null ? null : String(parsed.target_id);
      title =
        kind === "note"
          ? (
              await createNote(
                db,
                { title: parsed.title, body: parsed.body, tags: parsed.tags, notebook_id: target },
                now,
              )
            ).title
          : (
              await createTodo(
                db,
                {
                  title: parsed.title,
                  notes: parsed.body || null,
                  tags: parsed.tags,
                  list_id: target,
                },
                now,
              )
            ).title;
    } finally {
      db.close();
    }
  } catch (e) {
    return {
      reply: `Couldn't save that ${words.word}: ${e instanceof Error ? e.message : String(e)}. Your text is back in the box.`,
      error: true,
      restore: `/${kind} ${text}`,
    };
  }
  void drain().catch(() => undefined);

  const where = parsed.target_name ? `**${parsed.target_name}**` : words.home;
  const bits = [`${words.lead} — **${title}** → ${where}`];
  if (parsed.tags.length) bits.push(parsed.tags.map((t) => `\`#${t}\``).join(" "));
  let reply = bits.join(" · ");
  if (parsed.unknown_target) {
    const known = targets.map((t) => `\`@${t.name}\``).join(" · ");
    reply += `\n\nNo ${words.noun} called **${parsed.unknown_target}**, so it's in ${words.home} unfiled and the text kept as typed.`;
    if (known) reply += ` Try ${known}.`;
  }
  return { reply, error: false };
}
